import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { DndContext, pointerWithin, PointerSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"

import { getProductionStepsExecutionsSelector, getProductionStepsKitchenAreaSelector, getProductionStepsProductionDaySelector } from "../../reducers/Production/ProductionSteps"
import { formatPSEsToTiles } from "../../utils/tiles"
import { handleDrag, handleDrop, planInsert } from "../../actions/ProdPlanner/prodPlanner"
import { savePlannerTiles } from "../../actions/ProductionSteps/ProductionStepExecutions"
import { openAppMessageSnackBar } from "../../actions/Utils/app"
import { BLOCK_HEIGHT, UNASSIGNED_TILE_CONTENT_WIDTH, START_HOUR, BLOCK_TIME } from "../../utils/constant"
import { initializeDragState, calculateDragOffset, updateTileAppearanceOnDrag, isNoMachineCooling } from "../../utils/prodPlanner"
import { getMachineTypes } from "../../parseManager/machines/machineTypesManager"
import { getProductionStepExecution } from "../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"
import UnassignedTilesPanel from "../../components/ProdPlanner/UnassignedTilesPanel"
import ProdPlannerTable from "../../components/ProdPlanner/ProdPlannerTable"
import ProdPlannerTile from "../../components/ProdPlanner/ProdPlannerTile"

const ProdPlannerView = ({ onCancelRequest, onSaveRequest, onCoolingRequest, onHasChangesUpdate, onCoolingButtonStateChange }) => {
  const dispatch = useDispatch()
  const productionStepsExecutionsData = useSelector(getProductionStepsExecutionsSelector)
  const kitchenArea = useSelector(getProductionStepsKitchenAreaSelector)
  const productionDay = useSelector(getProductionStepsProductionDaySelector)

  // if kitchen area is "Prépa chaude", filter and sort by transformation type and use only COOLING OR COOKING
  const productionStepsExecutions = useMemo(() => {
    if (!productionStepsExecutionsData) return []
    if (kitchenArea?.name !== "Prépa chaude") return productionStepsExecutionsData
    return [...productionStepsExecutionsData]
      .filter(pse => ["COOLING", "COOKING"].includes(pse.productionStepSnapshot?.transformation))
      .sort((a, b) => a.productionStepSnapshot?.transformation.localeCompare(b.productionStepSnapshot?.transformation))
  }, [productionStepsExecutionsData, kitchenArea])


  // Tracks IDs of cooling tiles placed automatically — reused/overwritten on next "Générer", protected from manual moves
  const autoAssignedTileIds = useRef(new Set())

  // Local state
  const [linkedMachines, setLinkedMachines] = useState({})
  const [noMachineTypeIds, setNoMachineTypeIds] = useState(new Set())
  const [expandedTiles, setExpandedTiles] = useState({})
  const [tiles, setTiles] = useState([])
  const [previewTiles, setPreviewTiles] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeTile, setActiveTile] = useState(null)
  const [originalTiles, setOriginalTiles] = useState([])
  const [dragStartOffset, setDragStartOffset] = useState(null)
  const [columnWidth, setColumnWidth] = useState(116)
  const [recentlyDroppedTileId, setRecentlyDroppedTileId] = useState(null)
  const [isCoolingChainReady, setIsCoolingChainReady] = useState(false)

  useEffect(() => {
    getMachineTypes({ search: "Sans Machine" })
      .then(machineTypes => {
        setNoMachineTypeIds(new Set(
          machineTypes
            .filter(mt => mt.name?.trim().toLowerCase() === "sans machine")
            .map(mt => mt.objectId)
        ))
      })
      .catch(error => console.error("Error fetching Sans Machine machine types:", error))
  }, [])

  // Check if tiles have been modified
  const hasChanges = useMemo(() => {
    return JSON.stringify(tiles) !== JSON.stringify(originalTiles)
  }, [tiles, originalTiles])

  console.log("ProdPlannerView render", { linkedMachines})

  const showCoolingButton = useMemo(() => Object.keys(linkedMachines).length > 0, [linkedMachines])
  const coolingDisabled = useMemo(() => !isCoolingChainReady || !tiles.some(t => t.machine != null), [tiles, isCoolingChainReady])

  useEffect(() => {
    if (onCoolingButtonStateChange) {
      onCoolingButtonStateChange({ show: showCoolingButton, disabled: coolingDisabled })
    }
  }, [showCoolingButton, coolingDisabled, onCoolingButtonStateChange])

  // Notify parent when hasChanges updates
  useEffect(() => {
    if (onHasChangesUpdate) {
      onHasChangesUpdate(hasChanges)
    }
  }, [hasChanges, onHasChangesUpdate])

  // Handle cancel - reset tiles to original state
  const handleCancel = useCallback(() => {
    setTiles([...originalTiles])
  }, [originalTiles])

  // Handle save - persist current state via Redux action
  const handleSave = useCallback(async () => {
    try {
      await dispatch(savePlannerTiles(tiles, productionStepsExecutions, productionDay))
      // Update original tiles to match current state after successful save
      setOriginalTiles([...tiles])
    } catch (error) {
      // Error already handled by the action
      console.error("Save failed:", error)
    }
  }, [tiles, originalTiles, dispatch])

  /**
   * Register handlers with parent when they change
   */
  useEffect(() => {
    if (onCancelRequest) {
      onCancelRequest(handleCancel)
    }
  }, [onCancelRequest, handleCancel])

  useEffect(() => {
    if (onSaveRequest) {
      onSaveRequest(handleSave)
    }
  }, [onSaveRequest, handleSave])

  const blockToTime = (blockIndex) => {
    const totalMinutes = START_HOUR * 60 + blockIndex * BLOCK_TIME
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  const resolveLinkedCoolingTile = useCallback(async (currentTiles, startTile, maxHops = 5) => {
    let current = startTile
    const visited = new Set()
    for (let hops = 1; hops <= maxHops; hops++) {
      const nextId = current.ulteriorStep?.id || current.ulteriorStep?.objectId
      if (!nextId || visited.has(nextId)) return null
      visited.add(nextId)

      let next = currentTiles.find(t => t.objectId === nextId)
      if (!next) {
        try {
          const fetched = await getProductionStepExecution(nextId)
          next = {
            objectId: fetched.objectId,
            recipeName: fetched.recipeName,
            PSEName: fetched.productionStepSnapshot?.name,
            productionStepSnapshot: fetched.productionStepSnapshot,
            ulteriorStep: fetched.ulteriorStep,
          }
        } catch (error) {
          console.error("Error fetching linked step:", error)
          return null
        }
      }

      if (next.productionStepSnapshot?.transformation === "COOLING") {
        return { tile: next, hasIntermediateStep: hops > 1 }
      }
      current = next
    }
    return null
  }, [])

  const coolingChainCache = useRef(new Map())
  useEffect(() => {
    let cancelled = false
    const pending = tiles.filter(cookingTile =>
      cookingTile.ulteriorStep && !coolingChainCache.current.has(cookingTile.objectId)
    )

    if (pending.length === 0) {
      setIsCoolingChainReady(true)
      return
    }

    setIsCoolingChainReady(false)
    Promise.all(pending.map(async (cookingTile) => {
      const resolved = await resolveLinkedCoolingTile(tiles, cookingTile)
      if (!cancelled) coolingChainCache.current.set(cookingTile.objectId, resolved)
    })).then(() => {
      if (!cancelled) setIsCoolingChainReady(true)
    })

    return () => { cancelled = true }
  }, [tiles, resolveLinkedCoolingTile])

  const handleCoolingRequest = useCallback(async () => {
    let updatedTiles = [...tiles]
    const notifications = []
    const skipped = []
    const ignoredNoMachine = []

    const requiresManualPlacement = []

    const placedCookingTiles = tiles.filter(tile =>
      tile.machine != null && tile.start != null
    )

    for (const cookingTile of placedCookingTiles) {
      if (!cookingTile.ulteriorStep) continue

      let resolved = coolingChainCache.current.get(cookingTile.objectId)
      if (resolved === undefined) {
        setIsLoading(true)
        resolved = await resolveLinkedCoolingTile(updatedTiles, cookingTile)
        coolingChainCache.current.set(cookingTile.objectId, resolved)
        setIsLoading(false)
      }

      if (!resolved) continue

      const { tile: coolingTile, hasIntermediateStep } = resolved
      const coolingTileId = coolingTile.objectId

      if (isNoMachineCooling(coolingTile, noMachineTypeIds)) {
        ignoredNoMachine.push({
          recipeName: coolingTile.recipeName || cookingTile.recipeName || coolingTileId,
          stepName: coolingTile.PSEName || coolingTile.recipeName || coolingTileId,
          coolingTileId,
        })
        continue
      }

      if (hasIntermediateStep) {
        if (coolingTile.machine == null) {
          requiresManualPlacement.push({
            stepName: coolingTile.PSEName || coolingTile.recipeName || coolingTileId,
            coolingTileId,
          })
        }
        continue
      }

      const linkedMachine = linkedMachines[cookingTile.machine]
      if (!linkedMachine) continue
      const { id: coolingMachineId, name: coolingMachineName } = linkedMachine

      const duration = coolingTile.end != null && coolingTile.start != null
        ? coolingTile.end - coolingTile.start
        : coolingTile.duration || 2

      const newStart = cookingTile.end
      const newEnd = cookingTile.end + duration

      // Already exactly where it should be — re-mark as auto-assigned and move on silently
      if (coolingTile.machine === coolingMachineId && coolingTile.start === newStart && coolingTile.end === newEnd) {
        autoAssignedTileIds.current.add(coolingTileId)
        continue
      }

      // Skip tiles that were manually placed (not auto-assigned and already on a machine)
      const isUnassigned = coolingTile.machine == null
      const wasAutoAssigned = autoAssignedTileIds.current.has(coolingTileId)
      if (!isUnassigned && !wasAutoAssigned) {
        skipped.push({
          title: coolingTile.PSEName || coolingTile.recipeName || coolingTileId,
          machine: coolingMachineName,
        })
        continue
      }

      const placedCoolingTile = {
        ...coolingTile,
        machine: coolingMachineId,
        start: newStart,
        end: newEnd,
      }

      const base = updatedTiles.filter(t => t.objectId !== coolingTileId)
      const result = planInsert(base, placedCoolingTile)
      if (Array.isArray(result)) {
        const wouldDisplaceManual = result.some(t => {
          if (autoAssignedTileIds.current.has(t.objectId)) return false
          const original = base.find(o => o.objectId === t.objectId)
          return original &&
            original.machine === coolingMachineId &&
            original.start != null &&
            original.end != null &&
            original.start < newEnd &&
            newStart < original.end
        })

        if (wouldDisplaceManual) {
          if (wasAutoAssigned && coolingTile.machine != null) {
            updatedTiles = updatedTiles.map(t =>
              t.objectId === coolingTileId ? { ...t, machine: null, start: null, end: null } : t
            )
            autoAssignedTileIds.current.delete(coolingTileId)
          }
          skipped.push({
            title: coolingTile.PSEName || coolingTile.recipeName || coolingTileId,
            machine: coolingMachineName,
            reason: "conflict",
          })
          continue
        }

        updatedTiles = result
        autoAssignedTileIds.current.add(coolingTileId)
        notifications.push({
          title: coolingTile.PSEName || coolingTile.recipeName || coolingTileId,
          machine: coolingMachineName,
          start: blockToTime(newStart),
          end: blockToTime(newEnd),
        })
      }
    }

    setIsLoading(false)
    setTiles(updatedTiles)

    if (notifications.length > 0) {
      dispatch(openAppMessageSnackBar(
        `${notifications.length} étape${notifications.length > 1 ? "s de refroidissement ont été générées" : " de refroidissement a été générée"} avec succès.`,
        "success",
        5000,
        {},
        "LIST"
      ))
    }

    const conflicts = skipped.filter(s => s.reason === "conflict")
    if (conflicts.length > 0) {
      const machineList = [...new Set(conflicts.map(s => s.machine))].join(", ")
      dispatch(openAppMessageSnackBar(
        `Attention, ${conflicts.length} étape${conflicts.length > 1 ? "s n'ont pas pu être placées" : " n'a pas pu être placée"} en raison de conflits manuels (${machineList}).`,
        "warning",
        7000,
        {},
        "LIST"
      ))
    }

    for (const s of skipped.filter(s => s.reason !== "conflict")) {
      dispatch(openAppMessageSnackBar(
        `${s.title} ignoré — déjà assigné manuellement sur ${s.machine}`,
        "warning",
        7000,
        {},
        "LIST"
      ))
    }

    const stepNamesByRecipe = new Map()
    for (const s of ignoredNoMachine) {
      if (!stepNamesByRecipe.has(s.recipeName)) stepNamesByRecipe.set(s.recipeName, new Map())
      stepNamesByRecipe.get(s.recipeName).set(s.coolingTileId, s.stepName)
    }
    for (const [recipeName, stepNamesById] of stepNamesByRecipe) {
      const stepNames = [...stepNamesById.values()]
      const stepLabel = stepNames.length > 1 ? "les étapes suivantes" : "l'étape suivante"
      dispatch(openAppMessageSnackBar(
        `${recipeName} - Refroidissement réalisé sans machine pour ${stepLabel}: ${stepNames.join(", ")}.`,
        "warning",
        7000,
        {},
        "LIST"
      ))
    }

    for (const s of requiresManualPlacement) {
      dispatch(openAppMessageSnackBar(
        `Une étape sépare la cuisson du refroidissement en cellule, à placer manuellement par l'opérateur pour l'étape suivante : ${s.stepName}`,
        "warning",
        7000,
        {},
        "LIST"
      ))
    }
  }, [tiles, linkedMachines, noMachineTypeIds, dispatch])

  useEffect(() => {
    if (onCoolingRequest) {
      onCoolingRequest(handleCoolingRequest)
    }
  }, [onCoolingRequest, handleCoolingRequest])

  // Drag sensor configuration - requires 5px movement before drag starts
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  )

  /**
   * Custom collision detection that prioritizes the unassigned panel
   * This ensures drops to unassigned panel work correctly even when overlapping the table
   */
  const customCollisionDetection = useCallback((args) => {
    // First check if pointer is within the unassigned panel
    const pointerCollisions = pointerWithin(args)
    const unassignedCollision = pointerCollisions.find(collision => collision.id === "unassigned")

    // If hovering over unassigned panel, only return that collision
    if (unassignedCollision) return [unassignedCollision]

    // Otherwise use pointer detection for table cells
    return pointerCollisions
  }, [])


  /**
   * Toggles expanded/collapsed state of a tile
   */
  const toggleTile = (id) => {
    setExpandedTiles((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  /**
   * Updates active tile dimensions when tiles change (e.g., during resize)
   * Note: Only depends on tiles, not activeTile, to avoid re-triggering during drag state updates
   */
  useEffect(() => {
    if (activeTile) {
      const updatedTile = tiles.find((t) => t.objectId === activeTile.objectId)
      if (updatedTile && (updatedTile.start !== activeTile.start || updatedTile.end !== activeTile.end)) {
        setActiveTile({ ...updatedTile, _isDraggedOverTable: activeTile._isDraggedOverTable })
      }
    }
  }, [tiles])

  /**
   * Handles drag start event
   */
  const handleDragStart = (event) => {
    initializeDragState(tiles, event, setActiveTile, setColumnWidth, setExpandedTiles, expandedTiles)

    const dragOffset = calculateDragOffset(event)
    setDragStartOffset(dragOffset)
  }

  /**
   * Handles drag end event and finalizes the drop
   */
  const handleDragEnd = (event) => {
    // Check if dropping to unassigned panel
    if (event.over?.id === "unassigned") {
      setRecentlyDroppedTileId(event.active.id)
      // Clear the highlight after animation completes (300ms animation + small buffer)
      setTimeout(() => setRecentlyDroppedTileId(null), 400)
    }

    // If a cooking tile is moved to unassigned, clear its auto-assigned cooling tile
    let coolingTileToReset = null
    if (event.over?.id === "unassigned") {
      const draggedTile = tiles.find(t => t.objectId === event.active.id)
      if (draggedTile?.ulteriorStep) {
        const coolingTileId = draggedTile.ulteriorStep.id || draggedTile.ulteriorStep.objectId
        if (autoAssignedTileIds.current.has(coolingTileId)) {
          coolingTileToReset = coolingTileId
          autoAssignedTileIds.current.delete(coolingTileId)
        }
      }
    }

    autoAssignedTileIds.current.delete(event.active.id)
    handleDrop(tiles, setTiles, setPreviewTiles)(event)

    if (coolingTileToReset) {
      setTiles(prev => prev.map(t =>
        t.objectId === coolingTileToReset ? { ...t, machine: null, start: null, end: null } : t
      ))
    }

    setActiveTile(null)
    setDragStartOffset(null)
  }

  /**
   * Handles drag move event and updates tile appearance based on hover target
   */
  const handleDragMove = (event) => {
    handleDrag(tiles, setPreviewTiles)(event)
    updateTileAppearanceOnDrag(activeTile, event, setActiveTile)
  }

  /**
   * Formats production step executions asynchronously when data changes
   */
  useEffect(() => {
    const formatTiles = async () => {
      if (!productionStepsExecutions) return

      setIsLoading(true)
      try {
        const formatted = await formatPSEsToTiles(productionStepsExecutions, { withIngredients: true, withPlannerData: true })
        setTiles(formatted)
        // Store original state for cancel functionality
        setOriginalTiles([...formatted])
      } catch (error) {
        console.error("Error formatting PSEs", error)
      } finally {
        setIsLoading(false)
      }
    }
    formatTiles()
  }, [productionStepsExecutions])

  return (
    <div style={styles.wrapper}>
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={(tiles ?? []).filter((s) => s && s.objectId).map((s) => s.objectId)}
          strategy={verticalListSortingStrategy}
        >
          {/* Left side: Title + Planner Table */}
          <div style={styles.tableContainer}>
            <ProdPlannerTable
              tiles={tiles}
              setTiles={setTiles}
              previewTiles={previewTiles}
              kitchenAreaId={kitchenArea.objectId}
              dragging={activeTile && activeTile.machine != null}
              isAnyTileDragging={!!activeTile}
              isLoading={isLoading}
              onLinkedMachinesReady={setLinkedMachines}
              linkedMachines={linkedMachines}
              onManualResize={(tileId) => autoAssignedTileIds.current.delete(tileId)}
            />
          </div>

          {/* Right side: Unassigned Tiles Panel */}
          <UnassignedTilesPanel
            tiles={tiles}
            setTiles={setTiles}
            expandedTiles={expandedTiles}
            toggleTile={toggleTile}
            isLoading={isLoading}
            isAnyTileDragging={!!activeTile}
            recentlyDroppedTileId={recentlyDroppedTileId}
            noMachineTypeIds={noMachineTypeIds}
          />
        </SortableContext>

        {/* Drag Overlay - shows the tile being dragged */}
        <DragOverlay
          dropAnimation={null}
        >
          {activeTile && dragStartOffset ? (
            <div style={{
              position: "relative",
              width: (() => {
                const isOverTable = activeTile._isDraggedOverTable
                const isAssignedTile = activeTile.machine != null

                // Use column width when over table, unassigned width when over panel
                if (isOverTable) return `${columnWidth}px`
                // If assigned tile dragged over unassigned panel, use unassigned width
                if (isAssignedTile && !isOverTable) return `${UNASSIGNED_TILE_CONTENT_WIDTH}px`
                return `${dragStartOffset.initialWidth}px`
              })(),
              transform: (() => {
                const isOverTable = activeTile._isDraggedOverTable
                const isUnassignedTile = activeTile.machine == null
                const isAssignedTile = activeTile.machine != null

                // Determine current width based on state
                const currentWidth = isOverTable && isUnassignedTile
                  ? columnWidth
                  : isAssignedTile && !isOverTable
                    ? UNASSIGNED_TILE_CONTENT_WIDTH
                    : isAssignedTile && isOverTable
                      ? columnWidth
                      : dragStartOffset.initialWidth

                const currentHeight = isOverTable && isUnassignedTile
                  ? BLOCK_HEIGHT * 2 - 1
                  : dragStartOffset.initialHeight

                const currentXOffset = dragStartOffset.offsetXPercentage * currentWidth
                const currentYOffset = dragStartOffset.offsetYPercentage * currentHeight

                const xAdjustment = dragStartOffset.initialXOffset - currentXOffset
                const yAdjustment = dragStartOffset.initialYOffset - currentYOffset

                return `translateX(${xAdjustment}px) translateY(${yAdjustment}px)`
              })()
            }}>
              <ProdPlannerTile
                tile={
                  // Show as assigned (table style) if _isDraggedOverTable is true
                  // Show as unassigned (panel style) if _isDraggedOverTable is false
                  activeTile._isDraggedOverTable === true
                    ? {
                      ...activeTile,
                      machine: 0,
                      start: 0,
                      end: activeTile.start != null && activeTile.end != null
                        ? activeTile.end - activeTile.start
                        : (activeTile.duration || 2),
                    }
                    : {
                      ...activeTile,
                      machine: null,
                      start: null,
                      end: null,
                    }
                }
                isExpanded={false}
                onToggle={() => { }}
                isInOverlay={true}
                tiles={tiles}
                setTiles={setTiles}
              />
            </div>
          ) : activeTile ? (
            <ProdPlannerTile
              tile={
                // Show as assigned (table style) if _isDraggedOverTable is true
                // Show as unassigned (panel style) if _isDraggedOverTable is false
                activeTile._isDraggedOverTable === true
                  ? {
                    ...activeTile,
                    machine: 0,
                    start: 0,
                    end: activeTile.start != null && activeTile.end != null
                      ? activeTile.end - activeTile.start
                      : (activeTile.duration || 2),
                  }
                  : {
                    ...activeTile,
                    machine: null,
                    start: null,
                    end: null,
                  }
              }
              isExpanded={false}
              onToggle={() => { }}
              isInOverlay={true}
              tiles={tiles}
              setTiles={setTiles}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  )
}

export default ProdPlannerView

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "row",
    flexGrow: 1,
    height: "100%",
    overflow: "hidden",
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    height: "100%",
    minWidth: 0,
    overflow: "auto",
  },
}